import { StorefrontQueries } from '@petplate/utils/shopify/queries';
import type {
  CartBuyerIdentityInput,
  CartLineInput,
  CartLineUpdateInput,
  ShopifyAttribute
} from '@petplate/utils/shopify/types';

export type ShopifyCartProviderProps = {
  children: React.ReactNode;
  onCreate?: () => void;
  onLinesAdd?: () => void;
  onLinesUpdate?: () => void;
  onLinesRemove?: () => void;
  onNoteUpdate?: () => void;
  onBuyerIdentityUpdate?: () => void;
  onAttributesUpdate?: () => void;
  onDiscountCodesUpdate?: () => void;
  onCreateComplete?: () => void;
  onLinesAddComplete?: (lines: CartLineInput[]) => void;
  onLinesUpdateComplete?: (lines: CartLineUpdateInput[]) => void;
  onLinesRemoveComplete?: () => void;
  onNoteUpdateComplete?: () => void;
  onBuyerIdentityUpdateComplete?: () => void;
  onAttributesUpdateComplete?: () => void;
  onDiscountCodesUpdateComplete?: () => void;
};

export type CartUpdateActionCallbacks = {
  cartCheckCb: () => Promise<unknown>;
  beforeCb?: () => Promise<void> | void;
  successCb?: () => Promise<void> | void;
};

export enum CartActionEnum {
  Create = 'cartCreate',
  Fetch = 'cart',
  AddLines = 'cartLinesAdd',
  UpdateLines = 'cartLinesUpdate',
  RemoveLines = 'cartLinesRemove',
  UpdateNote = 'cartNoteUpdate',
  UpdateBuyerIdentity = 'cartBuyerIdentityUpdate',
  UpdateAttributes = 'cartAttributesUpdate',
  UpdateDiscountCodes = 'cartDiscountCodesUpdate'
}

export type CartCreateAction = {
  type: CartActionEnum.Create;
  payload: {
    lines?: CartLineInput[];
    discountCodes?: string[];
    buyerIdentity?: CartBuyerIdentityInput;
  };
};

export type CartFetchAction = {
  type: CartActionEnum.Fetch;
  payload: {
    cartId: string;
  };
};

export type CartLinesAddAction = {
  type: CartActionEnum.AddLines;
  payload: {
    lines: CartLineInput[];
  };
};

export type CartLinesUpdateAction = {
  type: CartActionEnum.UpdateLines;
  payload: {
    lines: CartLineUpdateInput[];
  };
};

export type CartLinesRemoveAction = {
  type: CartActionEnum.RemoveLines;
  payload: {
    lineIds: string[];
  };
};

export type CartNoteUpdateAction = {
  type: CartActionEnum.UpdateNote;
  payload: {
    note: string;
  };
};

export type CartBuyerIdentityUpdateAction = {
  type: CartActionEnum.UpdateBuyerIdentity;
  payload: {
    buyerIdentity?: CartBuyerIdentityInput;
  };
};

export type CartAttributesUpdateAction = {
  type: CartActionEnum.UpdateAttributes;
  payload: {
    attributes: ShopifyAttribute[];
  };
};

export type CartDiscountCodesUpdateAction = {
  type: CartActionEnum.UpdateDiscountCodes;
  payload: {
    discountCodes: string[];
  };
};

export type CartAction =
  | CartLinesAddAction
  | CartLinesUpdateAction
  | CartLinesRemoveAction
  | CartNoteUpdateAction
  | CartBuyerIdentityUpdateAction
  | CartAttributesUpdateAction
  | CartDiscountCodesUpdateAction;

export const CartActionQueries = {
  [CartActionEnum.AddLines]: StorefrontQueries.CartAddLines,
  [CartActionEnum.UpdateLines]: StorefrontQueries.CartUpdateLines,
  [CartActionEnum.RemoveLines]: StorefrontQueries.CartRemoveLines,
  [CartActionEnum.UpdateNote]: StorefrontQueries.CartUpdateNote,
  [CartActionEnum.UpdateBuyerIdentity]: StorefrontQueries.CartUpdateBuyerIdentity,
  [CartActionEnum.UpdateAttributes]: StorefrontQueries.CartUpdateAttributes,
  [CartActionEnum.UpdateDiscountCodes]: StorefrontQueries.CartUpdateDiscountCodes
};
